/**
 * Le sac, pensé comme celui de Dofus : des onglets par catégorie, un tri, et
 * un panneau de détail. L'équipement y est une liste d'exemplaires,
 * chacun avec son jet, là où les ressources restent des quantités.
 */
import type { Ctx } from "../contexte.ts"
import { sousOnglets } from "../contexte.ts"
import { esc, nb, feuille } from "../fmt.ts"
import { icone } from "../sprites.ts"
import { item, type Item } from "../../sim/contenu.ts"
import { perfection, slotDe, craftable, broyage, portable, estConsommable, LIBELLE_SLOT, type Recette, type Slot } from "../../sim/objets.ts"
import { lignesJet, lignesFourchette } from "../jet.ts"
import type { Porte } from "../../sim/objets.ts"
import { forgeable } from "./forge.ts"
import { panoplieDeLObjet } from "./panoplie.ts"
import { vueMarche } from "./marche.ts"
import { prixVente, valeurLot } from "../../sim/recolte.ts"
import { plafondCoffre, combienCraftable, LOT_MAX } from "../../sim/simulate.ts"
import { indexer, origine, libelleOrigine, destination, utilisations } from "../../sim/provenance.ts"
import { compteTurquoise, effetsVariables, NIVEAU_PARI, EFFETS_PARI } from "../../sim/dofus.ts"

export const SOUS_SAC: [string, string][] = [
  ["equipement", "Équipement"], ["ressources", "Ressources"], ["atelier", "Atelier"],
  // Le marché est rangé avec le sac : c'est là que sont les marchandises, et
  // c'est de là qu'on décide de vendre plutôt que de crafter (GDD §7).
  ["marche", "Hôtel des Ventes"],
]

export type Tri = "nom" | "niveau" | "unitaire" | "lot" | "quantite" | "perfection"
export const TRIS: [Tri, string][] = [
  ["niveau", "Niveau"], ["nom", "Nom"],
  ["unitaire", "Prix unité"], ["lot", "Prix du lot"], ["quantite", "Quantité"],
]

/** Le coffre n'a ni quantité ni prix du lot : chaque pièce est un exemplaire. */
const TRIS_EQ: [Tri, string][] = [
  ["niveau", "Niveau"], ["perfection", "Perfection"], ["nom", "Nom"], ["unitaire", "Prix"],
]
const triCoffre = (c: Ctx): Tri => {
  const t = (c as Ctx & { tri: Tri }).tri
  return TRIS_EQ.some(([k]) => k === t) ? t : "niveau"
}

export function vueSac(c: Ctx): string {
  const sous = SOUS_SAC.some(([k]) => k === c.sous) ? c.sous : "equipement"
  return `${sousOnglets(sous, SOUS_SAC)}${
    sous === "marche" ? vueMarche(c)
    : sous === "ressources" ? voletRessources(c)
    : sous === "atelier" ? voletAtelier(c)
    : panneauCoffre(c)}`
}

/**
 * Filtres du coffre : **tous les emplacements, toujours**.
 *
 * Je ne montrais d'abord que les catégories présentes, pour gagner de la place.
 * Mauvais calcul : une rangée qui change de contenu selon le coffre laisse
 * croire que « Armes » ou « Familier » n'existent pas. Les catégories vides sont
 * désormais présentes mais désactivées, ce qui informe au lieu de cacher.
 */
export const TYPES_EQ: [cle: string, libelle: string, slots: Slot[]][] = [
  ["coiffe", "Coiffe", ["coiffe"]],
  ["cape", "Cape", ["cape"]],
  ["amulette", "Amulette", ["amulette"]],
  ["anneau", "Anneau", ["anneau1", "anneau2"]],
  ["ceinture", "Ceinture", ["ceinture"]],
  ["bottes", "Bottes", ["bottes"]],
  ["arme", "Arme", ["arme"]],
  ["bouclier", "Bouclier", ["bouclier"]],
  ["familier", "Familier", ["familier"]],
]

/**
 * Le coffre, présenté comme les ressources : une grille, puis un détail.
 *
 * Exporté parce que l'écran d'équipement affiche le même coffre sous la poupée.
 * Il y était d'abord réduit à une grille nue, sans tri ni recherche, avec un
 * renvoi vers le sac : deux présentations du même contenu, dont une amputée.
 */
export function panneauCoffre(c: Ctx): string {
  const total = c.etat.coffre.length
  const plafond = plafondCoffre(c.etat, c.contenu)
  // Un coffre plein ne mange plus ce qu'on garde — mais le butin qui n'entre
  // plus repart en kamas, et ça, il faut le dire. Un joueur a perdu ses
  // familiers avant qu'on ne change la règle : le silence était le vrai défaut.
  const plein = total >= plafond
  const presque = !plein && total >= plafond - 10
  return `<section class="carte">
    <h2>Coffre <em class="${plein ? "plein" : presque ? "presque" : ""}">${total} / ${plafond} pièces</em></h2>
    ${plein
      ? `<p class="secur alerte">Coffre plein. <b>Rien de ce que tu gardes ne sera supprimé</b>, mais
         le butin d'équipement qui tombe est revendu à l'arrivée. Vends ou broie quelques pièces pour
         faire de la place — la sélection multiple est là pour ça.</p>`
      : presque
        ? `<p class="aide">Plus que ${plafond - total} place${plafond - total > 1 ? "s" : ""}. Au-delà,
           le butin d'équipement sera revendu à l'arrivée plutôt que de prendre la place de ce que tu gardes.</p>`
        : ""}
    <input class="recherche" id="rechercheCoffre" type="search" placeholder="Chercher une pièce…"
           value="${esc(c.recherche)}" autocomplete="off" />
    ${filtresCoffre(c)}
    <nav class="tris"><span>Trier</span>${TRIS_EQ.map(([k, lib]) =>
      `<button data-tri="${k}" aria-current="${k === triCoffre(c)}">${lib}</button>`).join("")}
      ${c.enLot ? "" : `<button class="mini lotEntree" id="ouvrirLot" title="Vendre ou broyer plusieurs pièces d'un coup">Sélection</button>`}</nav>
    <div id="coffre">${contenuCoffre(c)}</div>
  </section>`
}

function filtresCoffre(c: Ctx): string {
  const compte = new Map<string, number>()
  for (const x of c.etat.coffre) {
    const it = item(c.contenu, x.item)
    const sl = it ? slotDe(it) : null
    if (!sl) continue
    const cat = TYPES_EQ.find(([, , slots]) => slots.includes(sl))
    if (cat) compte.set(cat[0], (compte.get(cat[0]) ?? 0) + 1)
  }
  const actif = TYPES_EQ.some(([k]) => k === c.filtre) ? c.filtre : "tout"
  const bouton = (k: string, lib: string, n: number | null) =>
    `<button data-filtre="${k}" aria-current="${k === actif}"${n === 0 ? " disabled" : ""}>${lib}${
      n ? ` <em>${n}</em>` : ""}</button>`
  return `<nav class="filtres">
    ${bouton("tout", "Tout", c.etat.coffre.length || null)}
    ${TYPES_EQ.map(([k, lib]) => bouton(k, lib, compte.get(k) ?? 0)).join("")}
  </nav>`
}

/**
 * La grille et son détail, rafraîchis à part : reconstruire le panneau entier à
 * chaque frappe détruirait le champ de recherche et renverrait son curseur au
 * début. C'est le même piège que dans l'atelier.
 */
export function contenuCoffre(c: Ctx): string {
  const q = c.recherche.trim().toLowerCase()
  const cat = TYPES_EQ.find(([k]) => k === c.filtre)

  const pieces = piecesAffichees(c)

  if (pieces.length === 0) {
    return `<p class="aide">${
      q ? `Aucune pièce pour « ${esc(c.recherche)} ».`
      : cat ? "Rien dans cette catégorie."
      : "Aucune pièce en réserve. Les crafts non portés, les pièces déséquipées et celles trouvées sur les monstres arrivent ici."
    }</p>`
  }

  const choisie = pieces.some(({ x }) => x.uid === c.pieceChoisie)
  const garde = piecesAGarder(c)
  return `${barreLot(c, pieces, garde)}
  <div class="grille ${choisie ? "avecfeuille" : ""} ${c.enLot ? "enLot" : ""}">${pieces.map(({ x, it }) => {
    const pct = perfection(it, x)
    const verrou = !portable(it, c.p?.niveau ?? 1)
    const coche = c.lotPieces.has(x.uid)
    const aGarder = garde.has(x.uid)
    return `<button class="slot plein ${c.pieceChoisie === x.uid ? "choisi" : ""} ${verrou ? "verrou" : ""}
      ${coche ? "coche" : ""} ${aGarder ? "garde" : ""}"
      data-piece="${x.uid}" title="${esc(it.nom)}, ${pct} %${verrou ? ` — niveau ${it.niveau} requis` : ""}${
        aGarder ? " — la meilleure que tu aies pour cet emplacement" : ""}">
      <span class="ic" style="${icone("items", it.id, 40)}"></span>
      <span class="pf ${pct >= 90 ? "haut" : pct < 40 ? "bas" : ""}">${pct}%</span>
      ${verrou ? `<span class="niv">${it.niveau}</span>` : ""}
      ${c.enLot ? `<span class="case">${coche ? "✓" : ""}</span>` : ""}
      ${aGarder ? `<span class="mieux" title="La meilleure que tu aies pour cet emplacement">★</span>` : ""}
    </button>`
  }).join("")}</div>
  ${c.enLot ? "" : detailCoffre(c)}`
}

/** Les pièces du coffre visibles à l'écran : filtres, recherche et tri compris. */
function piecesAffichees(c: Ctx): { x: Porte; it: NonNullable<ReturnType<typeof item>> }[] {
  const { etat, contenu } = c
  const q = c.recherche.trim().toLowerCase()
  const cat = TYPES_EQ.find(([k]) => k === c.filtre)
  const tri = triCoffre(c)
  return etat.coffre
    .map((x) => ({ x, it: item(contenu, x.item)! }))
    .filter((o) => o.it)
    .filter((o) => !cat || cat[2].includes(slotDe(o.it) as Slot))
    .filter((o) => !q || o.it.nom.toLowerCase().includes(q))
    .sort((a, b) =>
      tri === "nom" ? a.it.nom.localeCompare(b.it.nom)
      : tri === "perfection" ? perfection(b.it, b.x) - perfection(a.it, a.x)
      : tri === "unitaire" || tri === "lot" ? prixVente(b.it) - prixVente(a.it)
      : b.it.niveau - a.it.niveau || perfection(b.it, b.x) - perfection(a.it, a.x))
}

/**
 * Les pièces que « Tout » laisse de côté.
 *
 * Une seule par emplacement : **la meilleure du coffre**, et seulement si elle
 * vaut mieux que celle qu'on porte — ou si l'emplacement est vide.
 *
 * La première version protégeait toute pièce meilleure que l'équipée. C'était
 * inutilisable : un personnage nu voyait ses quarante pièces protégées, et un
 * joueur qui rentre de farm avec huit anneaux les gardait tous les huit alors
 * qu'un seul l'intéresse. Vendre en lot est irréversible, mais un garde-fou qui
 * bloque tout ne protège plus rien — on le contourne, et on vend à l'aveugle.
 */
function piecesAGarder(c: Ctx): Set<string> {
  const garde = new Set<string>()
  if (!c.p) return garde
  const meilleure = new Map<Slot, { x: Porte; it: NonNullable<ReturnType<typeof item>> }>()

  for (const x of c.etat.coffre) {
    const it = item(c.contenu, x.item)
    const slot = it ? slotDe(it) : null
    if (!it || !slot || !portable(it, c.p.niveau)) continue
    const tenante = meilleure.get(slot)
    if (!tenante || it.niveau > tenante.it.niveau
        || (it.niveau === tenante.it.niveau && perfection(it, x) > perfection(tenante.it, tenante.x))) {
      meilleure.set(slot, { x, it })
    }
  }

  for (const [slot, o] of meilleure) {
    const actuel = c.p.equipement[slot]
    const itActuel = actuel ? item(c.contenu, actuel.item) : null
    const mieux = !actuel || !itActuel
      || o.it.niveau > itActuel.niveau
      || (o.it.niveau === itActuel.niveau && perfection(o.it, o.x) > perfection(itActuel, actuel))
    if (mieux) garde.add(o.x.uid)
  }
  return garde
}

/**
 * La barre de sélection multiple (GDD §21).
 *
 * Elle annonce ce qui va partir et ce que ça rapporte **avant** de cliquer :
 * une vente ne se rattrape pas, et un bouton qui ne dit pas combien de pièces il
 * emporte n'est pas un bouton sur lequel on ose appuyer.
 */
function barreLot(c: Ctx, pieces: { x: Porte; it: NonNullable<ReturnType<typeof item>> }[], garde: Set<string>): string {
  // Repliée, elle n'existe pas : l'entrée du mode vit dans la rangée de tri.
  if (!c.enLot) return ""
  const visibles = pieces.map((o) => o.x.uid)
  const cochees = pieces.filter((o) => c.lotPieces.has(o.x.uid))
  const gain = cochees.reduce((a, o) => a + prixVente(o.it), 0)
  const sans = pieces.filter((o) => !garde.has(o.x.uid))

  return `<nav class="lotBarre">
    <div class="lotChoix">
      <button class="mini" data-lotcoffre="tout" ${sans.length === 0 ? "disabled" : ""}
        title="Coche tout ce qui est affiché, sauf les pièces meilleures que celles portées">Tout (${sans.length})</button>
      <button class="mini" data-lotcoffre="rien" ${cochees.length === 0 ? "disabled" : ""}>Rien</button>
      <button class="mini" id="fermerLot">Terminer</button>
    </div>
    <div class="lotActions">
      <span class="compte">${cochees.length} pièce${cochees.length > 1 ? "s" : ""}${
        cochees.length > 0 ? ` · ${nb(gain)} kamas` : ""}</span>
      <button class="mini primaire" id="vendreLot" ${cochees.length === 0 ? "disabled" : ""}>Vendre</button>
      <button class="mini" id="broyerLot" ${cochees.length === 0 ? "disabled" : ""}>Broyer</button>
    </div>
    ${visibles.length > sans.length
      ? `<p class="aide">★ ${visibles.length - sans.length} pièce${visibles.length - sans.length > 1 ? "s sont les meilleures" : " est la meilleure"} que tu aies pour leur emplacement : « Tout » les laisse de côté, mais tu peux les cocher toi-même.</p>`
      : ""}
  </nav>`
}


/**
 * Ce que rend le broyage. En version courte pour tenir sur le bouton : le
 * panneau de détail a déjà demandé un défilement de trop une fois, une ligne
 * d'explication en plus le ramènerait hors de l'écran.
 */
export function rendu(c: Ctx, it: NonNullable<ReturnType<typeof item>>, court = false): string {
  const parts = Object.entries(broyage(it)).map(([id, n]) => {
    const nom = item(c.contenu, Number(id))?.nom ?? "?"
    return court ? `${n} ${nom.slice(0, 4)}.` : `${n} ${nom}`
  })
  return court ? parts.join(" + ") : parts.join(" et ")
}

/** Le détail d'une pièce du coffre : son jet, et la comparaison avec la portée. */
function detailCoffre(c: Ctx): string {
  const { etat, contenu, p } = c
  const x = c.pieceChoisie ? etat.coffre.find((o) => o.uid === c.pieceChoisie) : undefined
  const it = x ? item(contenu, x.item) : null
  if (!x || !it) return `<p class="aide">Touche une pièce pour voir son jet.</p>`

  const pct = perfection(it, x)
  const base = slotDe(it)
  const trop = !portable(it, p?.niveau ?? 1)
  const actuel = p && base ? p.equipement[base] : undefined
  const itActuel = actuel ? item(contenu, actuel.item) : null

  return feuille(`<div class="detail">
    <div class="entete">
      <span class="ic" style="${icone("items", it.id, 48)}"></span>
      <div class="ident">
        <b>${esc(it.nom)}</b>
        <span>${esc(it.type || "—")} · niv ${it.niveau} · <b class="pct">${pct} % parfait</b></span>
      </div>
    </div>
    ${lignesJet(contenu, x)}
    ${itActuel && actuel
      ? `<p class="compare ${pct >= perfection(itActuel, actuel) ? "mieux" : "moins"}">En place : ${esc(itActuel.nom)}, ${perfection(itActuel, actuel)} %. ${pct >= perfection(itActuel, actuel) ? "Ce jet est meilleur." : "Ce jet est moins bon."}</p>`
      : `<p class="aide">Emplacement ${base ? esc(LIBELLE_SLOT[base]) : "—"} libre.</p>`}
    ${trop ? `<p class="secur alerte">Il te faut le niveau ${it.niveau} pour porter cette pièce. Tu es niveau ${p?.niveau ?? 1}.</p>` : ""}
    ${panoplieDeLObjet(c, it.id)}
    <div class="actions">
      ${base && !trop ? `<button class="primaire" data-equiper="${x.uid}">Équiper</button>` : ""}
      <button data-vendreq="${x.uid}">Vendre ${nb(prixVente(it))} k</button>
      <button data-broyer="${x.uid}" title="Broyer rend ${esc(rendu(c, it))}, seule source de ces matériaux">Broyer ${esc(rendu(c, it, true))}</button>
      ${forgeable(contenu, x)
        ? `<button data-ouvrirforge="${x.uid}" title="Pousser une ligne de son jet contre des kamas">Forgemagie</button>` : ""}
      <button data-vendrehdv="${x.item}" data-uid="${x.uid}"
        title="Mettre cette pièce en vente à l'Hôtel des Ventes">Vendre à l'HDV</button>
    </div>
  </div>`)
}

/** Catégories de ressources, à la manière des filtres du sac Dofus. */
/**
 * Les catégories du sac.
 *
 * Un joueur a demandé où passaient les matériaux d'une pièce broyée. Ils étaient
 * bien en réserve — mais « Pierre précieuse » ne correspondait à aucune
 * catégorie, si bien que les Pépites n'existaient que dans « Tout », au niveau 1,
 * donc tout en bas d'une liste triée par niveau décroissant. Introuvables.
 *
 * Le trou était général : **1 851 objets répartis sur 70 types** n'avaient
 * aucune catégorie. « Autres » les rattrape tous — c'est le filet, et il est
 * calculé par élimination pour qu'aucun type nouveau ne puisse à nouveau
 * disparaître silencieusement.
 */
const CATEGORIES: [cle: string, libelle: string, test: (t: string) => boolean][] = [
  ["tout", "Tout", () => true],
  ["recolte", "Récolte", (t) => /céréale|plante|bois|minerai|poisson|fleur|graine/i.test(t)],
  ["butin", "Butin", (t) => /peau|laine|poudre|patte|aile|oeil|œil|corne|dent|os|cuir|plume|queue|poil|étoffe|liquide|ressource diverse/i.test(t)],
  // Ce que rend le broyage, et ce qui sert à forger : la réponse à la question
  // posée, et une catégorie qu'on pense à ouvrir.
  ["forge", "Forge", (t) => /pierre précieuse|pierre brute|pierre d'âme|rune|éclat/i.test(t)],
  ["clef", "Clés", (t) => /clef|clé|essence de gardien/i.test(t)],
  ["conso", "Consommables", (t) => /potion|pain|poisson comestible|viande|friandise|parchemin|boisson/i.test(t)],
  ["autres", "Autres", () => true],
]

/** Les catégories nommées, hors « Tout » et hors le filet « Autres ». */
const CATEGORIES_NOMMEES = CATEGORIES.slice(1, -1)

/** Dans quelle catégorie tombe ce type ? « Autres » quand aucune ne le prend. */
function categorieRessource(type: string): string {
  return CATEGORIES_NOMMEES.find(([, , test]) => test(type))?.[0] ?? "autres"
}

function voletRessources(c: Ctx): string {
  const { etat, contenu, filtre } = c
  const cat = CATEGORIES.find(([k]) => k === filtre) ?? CATEGORIES[0]!
  return `<section class="carte">
    <h2>Ressources <em>${nb(valeurLot(contenu, etat.inventaire))} k au total</em></h2>
    <input class="recherche" id="rechercheRes" type="search" placeholder="Chercher une ressource…"
           value="${esc(c.recherche)}" autocomplete="off" />
    <nav class="filtres">${CATEGORIES.map(([k, lib]) =>
      `<button data-filtre="${k}" aria-current="${k === cat[0]}">${lib}</button>`).join("")}</nav>
    ${barreTri(c)}
    ${barreLotRessources(c)}
    <div id="ressources">${contenuRessources(c)}</div>
  </section>`
}

/** La même sélection multiple, côté ressources : ici le lot part en entier. */
function barreLotRessources(c: Ctx): string {
  const { etat, contenu } = c
  if (!c.enLot) return ""
  const cochees = [...c.lotRessources]
    .map((id) => ({ it: item(contenu, id), n: etat.inventaire[id] ?? 0 }))
    .filter((l) => l.it && l.n > 0)
  const gain = cochees.reduce((a, l) => a + prixVente(l.it!) * l.n, 0)
  return `<nav class="lotBarre">
    <div class="lotChoix">
      <button class="mini" data-lotres="tout">Tout</button>
      <button class="mini" data-lotres="rien" ${cochees.length === 0 ? "disabled" : ""}>Rien</button>
      <button class="mini" id="fermerLot">Terminer</button>
    </div>
    <div class="lotActions">
      <span class="compte">${cochees.length} ressource${cochees.length > 1 ? "s" : ""}${
        cochees.length > 0 ? ` · ${nb(gain)} kamas` : ""}</span>
      <button class="mini primaire" id="vendreLotRes" ${cochees.length === 0 ? "disabled" : ""}>Vendre les lots</button>
    </div>
    <p class="aide">La sélection vend <b>tout le stock</b> de chaque ressource cochée. Pour n'en vendre qu'une partie, ouvre-la et choisis la quantité.</p>
  </nav>`
}

/** La grille et son détail, remplacés seuls à chaque frappe (cf. `contenuCoffre`). */
export function contenuRessources(c: Ctx): string {
  const { etat, contenu, tri, filtre } = c as Ctx & { tri: Tri; filtre: string }
  const cat = CATEGORIES.find(([k]) => k === filtre) ?? CATEGORIES[0]!
  const q = c.recherche.trim().toLowerCase()
  const lignes = Object.entries(etat.inventaire)
    .map(([id, n]) => ({ it: item(contenu, Number(id))!, n }))
    // Une pièce d'équipement n'a rien à faire ici : elle vit au coffre, avec son
    // jet. Le moteur les y déplace, ce filtre couvre le temps d'un tick.
    .filter((l) => l.it && !slotDe(l.it)
      && (cat[0] === "tout" || categorieRessource(l.it.type || "") === cat[0]))
    .filter((l) => !q || l.it.nom.toLowerCase().includes(q))
  const ordonne = [...lignes].sort((a, b) =>
    tri === "nom" ? a.it.nom.localeCompare(b.it.nom)
    : tri === "unitaire" ? prixVente(b.it) - prixVente(a.it)
    : tri === "lot" ? prixVente(b.it) * b.n - prixVente(a.it) * a.n
    : tri === "quantite" ? b.n - a.n
    : b.it.niveau - a.it.niveau)

  return `${ordonne.length === 0
      ? `<p class="aide">${q ? `Aucune ressource pour « ${esc(c.recherche)} ».` : "Rien dans cette catégorie."}</p>`
      : `<div class="grille ${ordonne.some((l) => l.it.id === c.selection) ? "avecfeuille" : ""} ${c.enLot ? "enLot" : ""}">${ordonne.map((l) => `
        <button class="slot plein ${c.selection === l.it.id ? "choisi" : ""} ${c.lotRessources.has(l.it.id) ? "coche" : ""}"
          data-detail="${l.it.id}" title="${esc(l.it.nom)}">
          <span class="ic" style="${icone("items", l.it.id, 40)}"></span>
          <span class="qte" data-qteres="${l.it.id}">${nb(l.n)}</span>
          ${c.enLot ? `<span class="case">${c.lotRessources.has(l.it.id) ? "✓" : ""}</span>` : ""}
        </button>`).join("")}</div>`}
    ${c.enLot ? "" : detailRessource(c)}`
}

/**
 * La rangée de tri, avec l'entrée du mode sélection au bout.
 *
 * Repliée, elle ne prend **aucune ligne à elle** : le panneau de détail du
 * coffre tient déjà tout juste au-dessus de la barre de navigation, et soixante
 * pixels de plus le faisaient sortir de l'écran.
 */
function barreTri(c: Ctx): string {
  const tri = (c as Ctx & { tri: Tri }).tri
  return `<nav class="tris"><span>Trier</span>${TRIS.map(([k, lib]) =>
    `<button data-tri="${k}" aria-current="${k === tri}">${lib}</button>`).join("")}
    ${c.enLot ? "" : `<button class="mini lotEntree" id="ouvrirLot" title="Vendre ou broyer plusieurs objets d'un coup">Sélection</button>`}</nav>`
}

function detailRessource(c: Ctx): string {
  const it = c.selection !== null ? item(c.contenu, c.selection) : null
  const n = it ? c.etat.inventaire[it.id] ?? 0 : 0
  if (!it || n <= 0) return `<p class="aide">Touche un objet pour le détailler et le revendre.</p>`
  return feuille(`<div class="detail">
    <div class="entete">
      <span class="ic" style="${icone("items", it.id, 48)}"></span>
      <div class="ident"><b>${esc(it.nom)}</b><span>${esc(it.type || "Ressource")} · niveau ${it.niveau} ·
        <b data-stockres="${it.id}">${nb(n)}</b> en stock</span></div>
    </div>
    <div class="ligneXp"><span>Valeur unitaire</span><b>${nb(prixVente(it))} kamas</b></div>
    <div class="ligneXp"><span>Tout le lot</span><b data-lotres="${it.id}">${nb(prixVente(it) * n)} kamas</b></div>

    <!-- « Vendre 1 » ou « Vendre tout » ne laissaient aucun entre-deux, alors
         que l'arbitrage du §7 est justement là : liquider le surplus en gardant
         de quoi crafter. Tout tient sur une ligne — le champ n'a besoin que de
         quatre chiffres, et un pavé pleine largeur pour ça poussait le bouton
         de vente hors de l'écran. -->
    <div class="quantite">
      <span class="lbl">Vendre</span>
      <input id="qteVente" type="number" min="1" max="${n}" step="1" inputmode="numeric"
             aria-label="Combien en vendre" value="${Math.min(c.qteVente || 1, n)}" />
      <span class="sur">sur ${nb(n)}</span>
      <span class="reperes">
        ${[10, 100].filter((k) => k < n).map((k) =>
          `<button class="mini" data-qte="${k}">${k}</button>`).join("")}
        ${n > 4 ? `<button class="mini" data-qte="${Math.floor(n / 2)}">½</button>` : ""}
        <button class="mini" id="qteTout" data-qte="${n}">Tout</button>
      </span>
    </div>

    <div class="actions">
      <button class="primaire" data-vendreqte="${it.id}">Vendre ${nb(Math.min(c.qteVente || 1, n))} · ${
        nb(prixVente(it) * Math.min(c.qteVente || 1, n))} kamas</button>
      <button data-vendrehdv="${it.id}"
        title="Le comptoir rachète au prix courant ; un joueur paiera peut-être davantage">Vendre à l'HDV</button>
    </div>

    ${sertA(c, it.id)}
  </div>`)
}

/**
 * **Ce que cette ressource permet de fabriquer.**
 *
 * Le détail disait sa valeur et proposait de la vendre : rien sur ce qu'elle
 * sert. Deux joueurs ont buté sur le même mur à un jour d'écart — Balbouz
 * « voir à partir d'une ressource ce qu'on peut craft avec », Vyshu « je peux
 * faire quoi de l'avoine ? il manque quoi comme ingrédients ? ». L'Avoine entre
 * dans quatorze recettes, dont douze clés de donjon.
 *
 * On montre **ce qui manque**, parce que la question n'est jamais « que
 * pourrais-je fabriquer un jour » mais « qu'est-ce qu'il me manque maintenant ».
 * Replié par défaut : sur une ressource commune la liste est longue, et le
 * détail sert d'abord à vendre.
 */
function sertA(c: Ctx, id: number): string {
  const u = utilisations(c.contenu, id, c.etat.inventaire)
  if (u.length === 0) {
    return `<p class="aide">Cette ressource n'entre dans aucune recette du jeu : elle ne vaut que ses kamas.</p>`
  }
  const ouvert = c.sertAOuvert === id
  const prets = u.filter((x) => x.ok).length
  const entete = `<button class="lien sertA" data-serta="${id}">Sert à ${nb(u.length)} recette${
    u.length > 1 ? "s" : ""}${prets > 0 ? ` · <b>${prets} réalisable${prets > 1 ? "s" : ""}</b>` : ""} ${ouvert ? "▴" : "▾"}</button>`
  if (!ouvert) return entete

  return entete + `<div class="listeSertA">${u.slice(0, 40).map((x) => `
    <button class="rangeeSertA ${x.ok ? "prete" : ""}" data-verscraft="${esc(c.contenu.items[String(x.resultat)]?.nom ?? "")}"
      title="Ouvrir la recette à l'atelier">
      <span class="ic" style="${icone("items", x.resultat, 32)}"></span>
      <span class="txt">
        <span class="nom">${esc(x.nom)} <em>niv ${x.niveau}</em></span>
        <span class="det">${x.ok
          ? "réalisable maintenant"
          : `manque ${x.manque.slice(0, 3).map((m) => `${nb(m.reste)} ${esc(m.nom)}`).join(", ")}${
              x.manque.length > 3 ? ` et ${x.manque.length - 3} autre${x.manque.length - 3 > 1 ? "s" : ""}` : ""}`}</span>
      </span>
      <span class="qte">×${nb(x.qte)}</span>
    </button>`).join("")}${u.length > 40 ? `<p class="aide">et ${nb(u.length - 40)} autres recettes</p>` : ""}</div>`
}

function voletAtelier(c: Ctx): string {
  const { etat, contenu } = c
  if (etat.enAttente) return voletJet(c)

  const q = c.recherche.trim().toLowerCase()
  const nbOk = contenu.recettes.filter((r) =>
    item(contenu, r.resultat) && craftable(r as Recette, etat.inventaire)).length

  return `<section class="carte">
    <h2>Atelier <em id="v-craftok">${nbOk} réalisable${nbOk > 1 ? "s" : ""}</em></h2>
    <p class="aide">Aucun niveau de métier requis : si tu as les matériaux, tu craftes. Toute la profondeur est dans le jet.</p>
    <input class="recherche" id="rechercheCraft" type="search" placeholder="Chercher une recette…"
           value="${esc(c.recherche)}" autocomplete="off" />
    <nav class="filtres">
      <button data-craftfiltre="ok" aria-current="${c.filtreCraft === "ok" && !q}">Réalisables</button>
      <button data-craftfiltre="neuf" aria-current="${c.filtreCraft === "neuf" && !q}">Jamais fabriquées</button>
      <button data-craftfiltre="" aria-current="${c.filtreCraft === "" || !!q}">Toutes</button>
    </nav>
    <nav class="filtres defilant">${EMPLACEMENTS.map(([k, lib]) =>
      `<button data-slotcraft="${k}" aria-current="${k === c.slotCraft}">${lib}</button>`).join("")}</nav>
    ${affinage(c)}
    <div id="recettes">${listeRecettes(c)}</div>
  </section>`
}

/**
 * La liste seule, rafraîchie à part.
 *
 * Reconstruire tout le panneau à chaque frappe détruisait le champ de
 * recherche : recréé, il perdait son curseur, qui repartait au début, et taper
 * « piou » donnait « uoip ». On ne remplace donc que cette liste.
 */
/**
 * Les emplacements de l'atelier.
 *
 * Les catégories d'avant regroupaient tout l'équipement sous une seule entrée,
 * ce qui ne répondait pas à la question qu'on se pose vraiment : « il me faut
 * une amulette ». On filtre donc par **emplacement réel**, celui-là même qui
 * décide où la pièce se porte, avec les catégories non portables à la suite.
 */
const EMPLACEMENTS: [cle: string, libelle: string, test: (it: Item) => boolean][] = [
  ["", "Tout", () => true],
  ["coiffe", "Coiffe", (it) => slotDe(it) === "coiffe"],
  ["cape", "Cape", (it) => slotDe(it) === "cape"],
  ["amulette", "Amulette", (it) => slotDe(it) === "amulette"],
  ["anneau1", "Anneau", (it) => slotDe(it) === "anneau1"],
  ["ceinture", "Ceinture", (it) => slotDe(it) === "ceinture"],
  ["bottes", "Bottes", (it) => slotDe(it) === "bottes"],
  ["arme", "Arme", (it) => slotDe(it) === "arme"],
  ["bouclier", "Bouclier", (it) => slotDe(it) === "bouclier"],
  ["familier", "Familier", (it) => slotDe(it) === "familier"],
  ["conso", "Vivres", (it) => estConsommable(it)],
  ["clef", "Clés", (it) => /clef|clé/i.test(it.type || "")],
  ["autre", "Le reste", (it) => !slotDe(it) && !estConsommable(it) && !/clef|clé/i.test(it.type || "")],
]

/**
 * Les caractéristiques qu'on peut exiger d'un résultat, **cumulables**.
 *
 * « Je veux une amulette qui donne de la Force ET de la Vitalité » était la
 * demande ; la recherche au clavier savait déjà le faire, mais rien ne le
 * disait. Chaque puce ajoute une exigence, et l'objet doit toutes les remplir.
 * Les libellés visent la statistique telle qu'elle est écrite dans les données.
 */
const CARACS_CRAFT: [cle: string, libelle: string, motif: RegExp][] = [
  ["vitalite", "Vitalité", /^vitalité$/i],
  ["force", "Force", /^force$/i],
  ["intelligence", "Intelligence", /^intelligence$/i],
  ["chance", "Chance", /^chance$/i],
  ["agilite", "Agilité", /^agilité$/i],
  ["sagesse", "Sagesse", /^sagesse$/i],
  ["puissance", "Puissance", /^puissance$/i],
  ["dommages", "Dommages", /^dommage/i],
  ["pa", "PA", /^PA$/],
  ["pm", "PM", /^PM$/],
  ["prospection", "Prospection", /^prospection$/i],
  // Demandé par Vyshu : les pods décident de ce qu'on peut porter, et
  // soixante-trois pièces en donnent — la ligne manquait à l'appel.
  ["pod", "Pods", /^pod$/i],
  // Demandé par Balbouz : 366 objets fabricables donnent de l'Invocation, et
  // rien ne permettait de les chercher.
  ["invocation", "Invocation", /^invocation$/i],
  ["soin", "Soin", /^soin$/i],
  ["resistance", "Résistances", /^% résistance/i],
]

const aLaCarac = (it: Item, cle: string): boolean => {
  const e = CARACS_CRAFT.find(([k]) => k === cle)
  return e ? it.effets.some((x) => e[2].test(x.stat ?? "")) : true
}

/** Bornes de niveau proposées : les paliers de Dofus, pas une réglette au pixel. */
export const PALIERS_NIVEAU = [0, 20, 40, 60, 80, 100, 120, 140, 160, 200]

/** Les lots proposés à côté de « Crafter », quand on a de quoi. */
const LOTS: [cle: string, libelle: string][] = [["10", "×10"], ["100", "×100"], ["max", "MAX"]]

/**
 * La recherche porte aussi sur les **statistiques du résultat**, et **cumule
 * les critères**.
 *
 * « Je veux une arme qui donne de la Force et de la Vitalité » est une question
 * au moins aussi fréquente que « je veux telle coiffe », et elle n'avait aucune
 * réponse : il fallait ouvrir les recettes une à une. Chaque mot de la
 * recherche doit correspondre — au nom de l'objet ou à l'une de ses
 * statistiques — ce qui permet de les combiner sans syntaxe à apprendre.
 */
const correspond = (it: NonNullable<ReturnType<typeof item>>, q: string): boolean => {
  const nom = it.nom.toLowerCase()
  // **Les malus ne comptent pas.** Les données préfixent d'un tiret ce qu'un
  // objet retire — « - Force », « -% Critique » — et une correspondance par
  // sous-chaîne les acceptait : chercher « force » remontait un anneau qui en
  // enlève. On cherche ce qu'un objet **donne**.
  const stats = it.effets.map((e) => (e.stat ?? "").toLowerCase())
    .filter((st) => !st.startsWith("-"))
  return q.split(/[\s+,]+/).filter(Boolean)
    .every((mot) => nom.includes(mot) || stats.some((st) => st.includes(mot)))
}

/**
 * Le panneau d'affinage : caractéristiques exigées et tranche de niveau.
 *
 * Replié par défaut — sur un téléphone, treize puces de caractéristiques
 * au-dessus des recettes noieraient l'essentiel — mais son résumé dit toujours
 * ce qui est actif, sans quoi on cherche pourquoi la liste est vide.
 */
/** Ce que les filtres d'affinage disent d'eux-mêmes : un compte et un résumé. */
export function etatAffinage(c: Ctx): { actifs: number; resume: string } {
  const [min, max] = c.nivCraft
  const parNiveau = min > 0 || max < 200
  const resume = [
    ...c.caracCraft.map((k) => CARACS_CRAFT.find(([x]) => x === k)?.[1] ?? k),
    parNiveau ? `niveau ${min}–${max}` : "",
  ].filter(Boolean).join(" · ")
  return { actifs: c.caracCraft.length + (parNiveau ? 1 : 0), resume: resume || "caractéristiques, niveau" }
}

function affinage(c: Ctx): string {
  const { actifs, resume } = etatAffinage(c)
  const [min, max] = c.nivCraft

  // Le compteur et le résumé existent toujours dans le DOM, même vides : ils se
  // réécrivent en place quand une puce change, sans reconstruire l'écran — ce
  // qui coûterait le curseur du champ de recherche.
  return `<div class="affinage">
    <button class="affiner" id="affiner" aria-expanded="${c.affiner}">
      <span>Affiner <b id="v-nbfiltres" ${actifs === 0 ? "hidden" : ""}>${actifs}</b></span>
      <span class="resume" id="v-resume">${esc(resume)}</span>
      <span class="chevron">${c.affiner ? "▴" : "▾"}</span>
    </button>
    ${c.affiner ? `<div class="volets">
      <p class="titrelot">Doit donner…</p>
      <nav class="filtres puces">${CARACS_CRAFT.map(([k, lib]) =>
        `<button data-caraccraft="${k}" aria-current="${c.caracCraft.includes(k)}">${lib}</button>`).join("")}</nav>
      <p class="titrelot">Niveau de l'objet</p>
      <div class="tranche">
        <label>de <select data-nivmin>${PALIERS_NIVEAU.map((n) =>
          `<option value="${n}" ${n === min ? "selected" : ""}>${n}</option>`).join("")}</select></label>
        <label>à <select data-nivmax>${PALIERS_NIVEAU.map((n) =>
          `<option value="${n}" ${n === max ? "selected" : ""}>${n}</option>`).join("")}</select></label>
        <button class="mini" id="videFiltres" ${actifs === 0 ? "hidden" : ""}>Tout effacer</button>
      </div>
    </div>` : ""}
  </div>`
}

export function listeRecettes(c: Ctx): string {
  const { etat, contenu } = c
  const idx = indexer(contenu)
  const q = c.recherche.trim().toLowerCase()
  const emp = EMPLACEMENTS.find(([k]) => k === c.slotCraft && k !== "")
  const [nivMin, nivMax] = c.nivCraft
  const recettes = contenu.recettes
    .map((r) => ({ r, it: item(contenu, r.resultat), ok: craftable(r as Recette, etat.inventaire) }))
    .filter((x) => x.it)
    .filter((x) => !q || correspond(x.it!, q))
    .filter((x) => !emp || emp[2](x.it!))
    .filter((x) => x.it!.niveau >= nivMin && x.it!.niveau <= nivMax)
    // Cumulatif : l'objet doit porter **toutes** les caractéristiques demandées.
    .filter((x) => c.caracCraft.every((k) => aLaCarac(x.it!, k)))
    .sort((a, b) => Number(b.ok) - Number(a.ok) || a.r.niveau - b.r.niveau)
  // **Jamais fabriquées** : demandé par Balbouz, et le jeu tenait déjà la
  // réponse — `recordsCraft` note le meilleur jet obtenu pour chaque objet, donc
  // une recette absente de cette table n'a jamais été faite. C'est la liste de
  // ce qui reste à découvrir.
  const visibles = q ? recettes
    : c.filtreCraft === "ok" ? recettes.filter((x) => x.ok)
    : c.filtreCraft === "neuf" ? recettes.filter((x) => etat.recordsCraft[x.r.resultat] === undefined)
    : recettes

  // Les épinglées passent devant, et **échappent au filtre comme à la
  // recherche** : c'est l'objectif qu'on suit, il doit rester sous les yeux.
  const toutes = contenu.recettes
    .map((r) => ({ r, it: item(contenu, r.resultat), ok: craftable(r as Recette, etat.inventaire) }))
  const epinglees = etat.epingles
    .map((id) => toutes.find((x) => x.r.resultat === id))
    .filter((x): x is NonNullable<typeof x> => Boolean(x?.it))
  const reste = visibles.filter((x) => !etat.epingles.includes(x.r.resultat))

  /**
   * Une fiche de recette, **repliée par défaut**.
   *
   * Elle affichait toujours sa fourchette de jet et la liste entière de ses
   * ingrédients : quatre à six lignes par recette, et il fallait défiler
   * interminablement pour parcourir l'atelier. Le détail ne s'ouvre plus qu'à
   * la demande, une recette à la fois ; l'en-tête résume ce qui manque.
   */
  const carte = ({ r, it, ok }: (typeof toutes)[number]) => {
    const pin = etat.epingles.includes(r.resultat)
    const ouverte = c.recetteOuverte === r.resultat
    // Une recette dont un ingrédient n'a aucune source ne se fera jamais : le
    // dire évite de la chercher.
    const impossible = r.ingredients.some((g) => origine(idx, g.item).type === "inconnue")
    const enMain = r.ingredients.filter((g) => (etat.inventaire[g.item] ?? 0) >= g.qte).length
    const combien = combienCraftable(r as Recette, etat.inventaire)

    return `
        <div class="recette ${ok ? "ok" : ""} ${pin ? "epingle" : ""} ${ouverte ? "ouverte" : ""}" data-recette="${r.resultat}">
          <button class="entete" data-ouvrirecette="${r.resultat}"
            aria-expanded="${ouverte}" title="${ouverte ? "Replier" : "Voir les ingrédients"}">
            <span class="ic" style="${icone("items", it!.id, 40)}"></span>
            <span class="txt">
              <span class="nom">${esc(it!.nom)}${
                // **Ce qui compte pour le Turquoise, dit sur la recette.**
                // Spiritus a cru le Dofus cassé après un craft parfait : « j'avais
                // un effet qui était pas variable, le PM ». La condition l'écrivait,
                // mais rien ne disait quels effets d'une recette se tirent au
                // hasard. Une règle qu'on ne peut pas vérifier sur l'objet qu'on
                // tient est une règle qu'on croit fausse.
                compteTurquoise(contenu, it!.id)
                  ? `<span class="pari" title="Un jet parfait ici compte pour le Dofus Turquoise : niveau ${
                      NIVEAU_PARI} ou plus et ${EFFETS_PARI} effets variables au moins (celui-ci en a ${
                      effetsVariables(contenu, it!.id)})">pari</span>`
                  : ""}</span>
              <span class="meta">${esc(it!.type || "—")} · niv ${it!.niveau}${
                // Un record de perfection ne veut rien dire sur un objet sans jet :
                // une clé de donjon affichait « record 100 % ».
                slotDe(it!) && etat.recordsCraft[it!.id] !== undefined
                  ? ` · record ${etat.recordsCraft[it!.id]} %` : ""} · <span
                class="ingcompte ${enMain === r.ingredients.length ? "ok" : ""}"
                >${enMain}/${r.ingredients.length} ingrédients</span></span>
            </span>
            <span class="chevron">${ouverte ? "▴" : "▾"}</span>
          </button>

          <button class="pin ${pin ? "on" : ""}" data-epingle="${r.resultat}"
            aria-pressed="${pin}" title="${pin ? "Retirer des objectifs" : "Mettre de côté"}">${pin ? "★" : "☆"}</button>

          <span class="action">${ok
            ? `<button class="mini primaire" data-craft="${r.resultat}">Crafter</button>
               ${combien > 1 ? `<span class="lots">${LOTS.filter(([n]) => n === "max" || Number(n) <= combien)
                 .map(([n, lib]) => {
                   // **Le bouton dit le nombre qu'il fabrique.** Il annonçait
                   // « MAX » et s'arrêtait à cinq cents : « btw quand tu craft,
                   // max c'est pas vraiment max mais c'est 500 » (Balbouz). Un
                   // libellé qui promet plus que le geste use la confiance qu'on
                   // a dans tous les autres.
                   const combienLot = n === "max" ? Math.min(combien, LOT_MAX) : Number(n)
                   const libelle = n === "max" ? `×${nb(combienLot)}` : lib
                   return `<button class="mini" data-craftlot="${r.resultat}" data-combien="${n}"
                     title="Fabriquer ${nb(combienLot)} d'un coup${
                       n === "max" && combien > LOT_MAX ? ` — le maximum par fournée est ${nb(LOT_MAX)}` : ""}">${libelle}</button>`
                 }).join("")}</span>` : ""}`
            : impossible
              ? `<span class="bloque impossible" title="Un ingrédient n'a aucune source dans le jeu">hors d'atteinte</span>`
              : `<span class="bloque">à compléter</span>`}</span>

          ${combien > 1 ? `<span class="possible">de quoi en faire ${nb(combien)}</span>` : ""}

          ${!ouverte ? "" : `${lignesFourchette(contenu, r.resultat)}
          ${panoplieDeLObjet(c, r.resultat)}
          <div class="ingredients">${r.ingredients.map((g) => {
            const gi = item(contenu, g.item)
            const dispo = etat.inventaire[g.item] ?? 0
            const assez = dispo >= g.qte
            const o = origine(idx, g.item)
            const ou = destination(o, gi?.nom ?? "")
            return `<div class="ing ${assez ? "ok" : "ko"}">
              <span class="pt" style="${icone("items", g.item, 24)}"></span>
              <span class="nm">${esc(gi?.nom ?? "?")}</span>
              ${ou
                ? `<button class="ou ${o.type} lien" data-aller="${esc(ou)}"
                     title="Aller y chercher ${esc(gi?.nom ?? "")}">${esc(libelleOrigine(o))} ›</button>`
                : `<span class="ou ${o.type}">${esc(libelleOrigine(o))}</span>`}
              <b data-stock="${g.item}">${nb(dispo)}<em>/${g.qte}</em></b>
            </div>`
          }).join("")}</div>`}
        </div>`
  }

  if (epinglees.length === 0 && reste.length === 0) {
    return `<p class="aide">${q ? `Aucune recette pour « ${esc(c.recherche)} ».` : "Rien de réalisable pour l'instant."}</p>`
  }
  return `${epinglees.length ? `<div class="recettes epinglees">
      <p class="titrelot">Mes objectifs</p>${epinglees.map(carte).join("")}</div>` : ""}
    ${reste.length ? `<div class="recettes">${reste.slice(0, 60).map(carte).join("")}</div>`
      : `<p class="aide">${q ? "Aucune autre recette pour cette recherche." : "Rien d'autre de réalisable pour l'instant."}</p>`}`
}

/** Le moment qui porte tout le système : on voit le jet, on décide. */
function voletJet(c: Ctx): string {
  const { etat, contenu, p } = c
  const porte = etat.enAttente!
  const it = item(contenu, porte.item)!
  const pct = perfection(it, porte)
  const base = slotDe(it)
  const actuel = p && base ? p.equipement[base] : undefined
  const itActuel = actuel ? item(contenu, actuel.item) : null

  return `<section class="carte jet">
    <div class="entete">
      <div class="avatar" style="${icone("items", it.id, 56)}"></div>
      <div class="ident"><h2>${esc(it.nom)}</h2><p class="aide">${esc(it.type)} · niv ${it.niveau}</p></div>
      <div class="kills"><span>${pct}</span><span>% parfait</span></div>
    </div>
    ${lignesJet(contenu, porte)}
    ${itActuel && actuel
      ? `<p class="compare ${pct >= perfection(itActuel, actuel) ? "mieux" : "moins"}">En place : ${esc(itActuel.nom)}, ${perfection(itActuel, actuel)} %. ${pct >= perfection(itActuel, actuel) ? "Ce jet est meilleur." : "Ce jet est moins bon."}</p>`
      : `<p class="aide">Emplacement ${base ? esc(LIBELLE_SLOT[base]) : "—"} libre.</p>`}
    ${!portable(it, p?.niveau ?? 1)
      ? `<p class="secur alerte">Il te faut le niveau ${it.niveau} pour la porter. Tu la gardes au coffre en attendant.</p>` : ""}
    <div class="actions">
      ${portable(it, p?.niveau ?? 1) ? `<button class="primaire" id="garder">Équiper</button>` : ""}
      <button id="ranger">Au coffre</button>
      <button id="jeter" class="danger">Jeter</button>
    </div>
  </section>`
}

/**
 * Ce que « Tout » coche, côté coffre.
 *
 * Exactement les pièces **affichées** — mêmes filtres, même recherche — moins
 * celles qui valent mieux que ce qu'on porte. Cocher ce qu'on ne voit pas serait
 * le meilleur moyen de vendre sans le savoir.
 */
export function piecesSelectionnables(c: Ctx): string[] {
  const garde = piecesAGarder(c)
  return piecesAffichees(c).filter((o) => !garde.has(o.x.uid)).map((o) => o.x.uid)
}

/** Ce que « Tout » coche, côté ressources : ce qui est affiché, sans exception. */
export function ressourcesVisibles(c: Ctx): number[] {
  const cat = CATEGORIES.find(([k]) => k === c.filtre) ?? CATEGORIES[0]!
  const q = c.recherche.trim().toLowerCase()
  return Object.entries(c.etat.inventaire)
    .map(([id, n]) => ({ it: item(c.contenu, Number(id))!, n }))
    .filter((l) => l.it && !slotDe(l.it) && l.n > 0
      && (cat[0] === "tout" || categorieRessource(l.it.type || "") === cat[0]))
    .filter((l) => !q || l.it.nom.toLowerCase().includes(q))
    .map((l) => l.it.id)
}
